export interface IUserGpxRouteSummary {
    gpxGuid: string;
    displayName: string;
    notes?: string;
    colourHex: string;
    lengthMetres?: number;
    isVisible: boolean;
    createdUtc: string;
    updatedUtc: string;
}

export interface IUserGpxRouteDetail extends IUserGpxRouteSummary {
    originalFileName?: string;
    gpxXml: string;
    tags?: string[];
    waypointCount?: number;
}

/**
 * Lightweight GPX route metadata used to build the sidebar on the map page.
 * Geometry for these routes is delivered to the map by the /features endpoint,
 * so the sidebar payload omits both `gpxXml` and any geometry to keep page-load
 * fast (it carries only the bounding box for zoom-to-route).
 */
export interface IUserGpxRouteSidebarItem {
    gpxGuid: string;
    displayName: string;
    notes?: string | null;
    colourHex: string;
    originalFileName?: string;
    lengthMetres?: number;
    isVisible: boolean;
    /** Bounding box in EPSG:4326 derived from the stored geometry. Null for routes with no geometry. */
    extentMinX?: number | null;
    extentMinY?: number | null;
    extentMaxX?: number | null;
    extentMaxY?: number | null;
}

/**
 * Slim result returned by the dedicated colour PATCH endpoint.
 * Only contains the fields that change — callers should merge this into
 * their existing cached route rather than replacing the whole record.
 */
export interface IGpxRouteColourResult {
    gpxGuid: string;
    colourHex: string;
    updatedUtc: string;
}

/**
 * Slim result returned by the visibility endpoint.
 */
export interface IGpxRouteVisibilityResult {
    gpxGuid: string;
    isVisible: boolean;
    updatedUtc: string;
}

/** A row in the paged GPX routes panel list. */
export interface IGpxRouteListItem extends IUserGpxRouteSummary {
    tags: string[];
    waypointCount: number;
    extentMinX?: number | null;
    extentMinY?: number | null;
    extentMaxX?: number | null;
    extentMaxY?: number | null;
}

export interface IGpxSortDescriptor {
    field: 'name' | 'created' | 'updated' | 'length';
    descending: boolean;
}

/** The active panel filter shared by the routes list, the map layer and waypoints. */
export interface IGpxFilterCriteria {
    search: string;
    tags: string[];
}

export interface IGpxListQuery {
    search?: string;
    tags?: string[];
    page: number;
    pageSize: number;
    sort: IGpxSortDescriptor[];
    /**
     * Ask the server for whichever page contains this route under the supplied
     * filter/sort, instead of `page`. The ordering depends on SQL Server collation
     * and NULL placement, so only the server can answer this reliably.
     */
    focusGuid?: string;
}

export interface IGpxListResult {
    items: IGpxRouteListItem[];
    totalCount: number;
    /** The page the server actually served — authoritative, especially with focusGuid. */
    page: number;
    pageSize: number;
}

export interface IGpxTagCount {
    tag: string;
    count: number;
}

export interface IGpxWaypointType {
    id: number;
    name: string;
    iconClass: string;
    markerSvg: string;
    colourHex: string;
    sortOrder: number;
}

export interface IGpxWaypoint {
    waypointGuid: string;
    gpxGuid: string;
    waypointTypeId: number;
    name: string;
    description?: string | null;
    lat: number;
    lon: number;
    elevationMetres?: number | null;
    sortOrder: number;
}

export interface IGpxWaypointUpsert {
    waypointGuid?: string | null;
    clientRef?: string | null;
    lat: number;
    lon: number;
    name: string;
    description?: string | null;
    waypointTypeId: number;
    sortOrder: number;
}

export interface IGpxEditSessionPayload {
    displayName?: string | null;
    notes?: string | null;
    colourHex?: string | null;
    /** [segment][point][lon, lat] in EPSG:4326. Null = geometry unchanged. */
    segments?: number[][][] | null;
    waypointUpserts?: IGpxWaypointUpsert[];
    waypointDeletes?: string[];
    /** Replacement tag set. Null/undefined = unchanged; empty = clear. */
    tags?: string[] | null;
}

export interface IGpxDrawCreatePayload {
    displayName: string;
    notes?: string | null;
    colourHex?: string | null;
    segments: number[][][];
    waypoints?: IGpxWaypointUpsert[];
    tags?: string[];
}

export interface IGpxSaveResult {
    success: boolean;
    errorCode?: string | null;
    errorMessage?: string | null;
    route: IUserGpxRouteSummary;
    waypoints: IGpxWaypoint[];
    clientRefMap: { [clientRef: string]: string };
}

export class GpxRouteApi {
    private readonly baseUrl: string = '/api/gpxroutes';

    public async getSidebarItems(signal?: AbortSignal): Promise<IUserGpxRouteSidebarItem[]> {
        const response = await fetch(`${this.baseUrl}/sidebar`, { credentials: 'same-origin', signal });
        await this.ensureOk(response);
        return response.json();
    }

    public async getRoute(gpxGuid: string, signal?: AbortSignal): Promise<IUserGpxRouteDetail> {
        const response = await fetch(`${this.baseUrl}/${gpxGuid}`, { credentials: 'same-origin', signal });
        await this.ensureOk(response);
        return response.json();
    }

    public async createRoute(payload: Partial<IUserGpxRouteDetail>, signal?: AbortSignal): Promise<IUserGpxRouteDetail> {
        const response = await fetch(this.baseUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal
        });
        await this.ensureOk(response);
        return response.json();
    }

    public async updateRoute(gpxGuid: string, payload: Partial<IUserGpxRouteDetail>, signal?: AbortSignal): Promise<IUserGpxRouteDetail> {
        const response = await fetch(`${this.baseUrl}/${gpxGuid}/update`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal
        });
        await this.ensureOk(response);
        return response.json();
    }

    public async updateRouteColour(gpxGuid: string, colourHex: string, signal?: AbortSignal): Promise<IGpxRouteColourResult> {
        const response = await fetch(`${this.baseUrl}/${gpxGuid}/colour`, {
            method: 'PATCH',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ colourHex }),
            signal
        });
        await this.ensureOk(response);
        return response.json();
    }

    public async deleteRoute(gpxGuid: string, signal?: AbortSignal): Promise<void> {
        const response = await fetch(`${this.baseUrl}/${gpxGuid}/delete`, {
            method: 'POST',
            credentials: 'same-origin',
            signal
        });
        if (response.status === 204 || response.status === 404) {
            return;
        }
        await this.ensureOk(response);
    }

    public async setVisibility(gpxGuid: string, visible: boolean, signal?: AbortSignal): Promise<IGpxRouteVisibilityResult> {
        const response = await fetch(`${this.baseUrl}/${gpxGuid}/visibility`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(visible),
            signal
        });
        await this.ensureOk(response);
        return response.json();
    }

    public async getRouteList(query: IGpxListQuery, signal?: AbortSignal): Promise<IGpxListResult> {
        const params = new URLSearchParams();
        if (query.search) {
            params.set('search', query.search);
        }
        if (query.tags && query.tags.length > 0) {
            params.set('tags', query.tags.join(','));
        }
        params.set('page', query.page.toString());
        params.set('pageSize', query.pageSize.toString());
        if (query.sort && query.sort.length > 0) {
            params.set('sort', query.sort.map(s => `${s.field}.${s.descending ? 'desc' : 'asc'}`).join(','));
        }
        if (query.focusGuid) {
            params.set('focusGuid', query.focusGuid);
        }

        const response = await fetch(`${this.baseUrl}/list?${params.toString()}`, { credentials: 'same-origin', signal });
        await this.ensureOk(response);
        return response.json();
    }

    public async getTagSummary(signal?: AbortSignal): Promise<IGpxTagCount[]> {
        const response = await fetch(`${this.baseUrl}/tags`, { credentials: 'same-origin', signal });
        await this.ensureOk(response);
        return response.json();
    }

    public async getWaypointTypes(signal?: AbortSignal): Promise<IGpxWaypointType[]> {
        const response = await fetch(`${this.baseUrl}/waypointtypes`, { credentials: 'same-origin', signal });
        await this.ensureOk(response);
        return response.json();
    }

    public async getWaypoints(visibleOnly: boolean = true, criteria?: IGpxFilterCriteria, signal?: AbortSignal): Promise<IGpxWaypoint[]> {
        const params = new URLSearchParams();
        params.set('visibleOnly', String(visibleOnly));
        if (criteria?.search) {
            params.set('search', criteria.search);
        }
        if (criteria?.tags && criteria.tags.length > 0) {
            params.set('tags', criteria.tags.join(','));
        }
        const response = await fetch(`${this.baseUrl}/waypoints?${params.toString()}`, { credentials: 'same-origin', signal });
        await this.ensureOk(response);
        return response.json();
    }

    public async getRouteWaypoints(gpxGuid: string, signal?: AbortSignal): Promise<IGpxWaypoint[]> {
        const response = await fetch(`${this.baseUrl}/${gpxGuid}/waypoints`, { credentials: 'same-origin', signal });
        await this.ensureOk(response);
        return response.json();
    }

    public async createDrawnRoute(payload: IGpxDrawCreatePayload, signal?: AbortSignal): Promise<IGpxSaveResult> {
        const response = await fetch(`${this.baseUrl}/draw`, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal
        });
        await this.ensureOk(response);
        return response.json();
    }

    public async saveEditSession(gpxGuid: string, payload: IGpxEditSessionPayload, signal?: AbortSignal): Promise<IGpxSaveResult> {
        const response = await fetch(`${this.baseUrl}/${gpxGuid}/editsession`, {
            method: 'PUT',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
            signal
        });
        await this.ensureOk(response);
        return response.json();
    }

    public async setTags(gpxGuid: string, tags: string[], signal?: AbortSignal): Promise<IGpxSaveResult> {
        const response = await fetch(`${this.baseUrl}/${gpxGuid}/tags`, {
            method: 'PATCH',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(tags),
            signal
        });
        await this.ensureOk(response);
        return response.json();
    }

    private async ensureOk(response: Response): Promise<void> {
        if (!response.ok) {
            let detail = '';
            try {
                const data = await response.json();
                detail = typeof data === 'string' ? data : JSON.stringify(data);
            } catch {
                detail = response.statusText;
            }
            throw new Error(`Request failed (${response.status}): ${detail}`);
        }
    }
}
